/**
 * Quality Question Dataset Builder
 *
 * PHASE 3 MIGRATION: Updated imports to use migrated paths.
 * - requiresVisual → policyEngine (via migrated visualRequiredRules wrapper)
 * - validateQuestion → still uses unifiedValidator (pending Step 3.10)
 * - scoreQuestion → still uses questionQualityEngine (pending Step 3.11)
 *
 * ARCHITECTURE v4.0 — Phase 3, Step 3.6
 */

import { Question } from '../../../types'
import { validateQuestion } from '../../../governance/unifiedValidator'
import { familyRequiresVisual } from '../../../governance/pipeline/policyEngine'
import { scoreQuestion } from '../../../governance/questionQualityEngine'
import { QualityIssue, QualityQuestionRecord } from './useQualityWorkspaceState'

function createIssue(
  category: QualityIssue['category'],
  severity: QualityIssue['severity'],
  message: string,
  questionId: string,
  index: number,
): QualityIssue {
  return {
    id: `${questionId}-${category}-${index}`,
    category,
    severity,
    message,
  }
}

export function buildQualityQuestionDataset(
  questions: Question[],
): QualityQuestionRecord[] {
  return questions.map((question) => {
    const issues: QualityIssue[] = []

    // ── Unified validation pipeline (single source of truth) ─────────────────
    const unified = validateQuestion(question)
    unified.issues.forEach((issue, index) => {
      const category =
        issue.domain === 'visual' || issue.domain === 'answer-leakage'
          ? 'visuals'
          : issue.domain === 'didactic' || issue.domain === 'quality'
            ? 'content'
            : 'governance'
      issues.push(
        createIssue(
          category,
          issue.severity as QualityIssue['severity'],
          issue.message,
          question.id,
          index,
        ),
      )
    })

    // ── Seed integrity checks (structural, not in unified pipeline) ───────────
    const hasEmptyOption = question.options.some((o) => !o || !o.trim())
    const hasDuplicateOption =
      new Set(question.options.map((o) => o.trim().toLowerCase())).size <
      question.options.length
    if (hasEmptyOption) {
      issues.push(
        createIssue(
          'seeds',
          'critical',
          'Contains empty answer options',
          question.id,
          issues.length,
        ),
      )
    }
    if (hasDuplicateOption) {
      issues.push(
        createIssue(
          'seeds',
          'warning',
          'Contains duplicate answer options',
          question.id,
          issues.length,
        ),
      )
    }

    // ── Visual coverage check ─────────────────────────────────────────────────
    const hasVisual = Boolean(
      question.stimulusAids?.length || question.supportAids?.length,
    )
    if (familyRequiresVisual(question.familyId) && !hasVisual) {
      issues.push(
        createIssue(
          'visuals',
          'critical',
          'Visual required but missing',
          question.id,
          issues.length,
        ),
      )
    }

    // ── Quality score (scoring layer on top of unified results) ───────────────
    const qualityResult = scoreQuestion(question)

    if (qualityResult.flags.some((f) => f.label === 'Generic feedback')) {
      issues.push(
        createIssue(
          'quality',
          'critical',
          'Boilerplate explanation — does not explain the concept or calculation (MASTER_2_2)',
          question.id,
          issues.length,
        ),
      )
    }
    if (qualityResult.flags.some((f) => f.label === 'No explanation')) {
      issues.push(
        createIssue(
          'quality',
          'critical',
          'Missing explanation text entirely (MASTER_2_2)',
          question.id,
          issues.length,
        ),
      )
    }
    if (qualityResult.flags.some((f) => f.label === 'Generic distractor')) {
      issues.push(
        createIssue(
          'quality',
          'warning',
          'Generic distractor explanations — must reference specific misconception (MASTER_2_3)',
          question.id,
          issues.length,
        ),
      )
    }
    if (qualityResult.flags.some((f) => f.label === 'Weak distractor')) {
      issues.push(
        createIssue(
          'quality',
          'warning',
          'Implausible distractor detected',
          question.id,
          issues.length,
        ),
      )
    }
    if (qualityResult.grade === 'poor') {
      issues.push(
        createIssue(
          'quality',
          'warning',
          `Poor quality score: ${qualityResult.totalScore}/100`,
          question.id,
          issues.length,
        ),
      )
    }

    return {
      question,
      issues,
      qualityScore: qualityResult.totalScore,
      qualityFlags: qualityResult.flags,
    }
  })
}
